// Sunrise, sunset and civil twilight (which bounds EASA night). At Finnish
// latitudes an event may not happen at all: 'alwaysUp' / 'alwaysDown'.

export type SunEventState = 'normal' | 'alwaysUp' | 'alwaysDown'

export interface SunEvents {
  state: SunEventState
  rise: string | null
  set: string | null
}

export interface SunTimes {
  date: string
  // -0.833°: upper limb on the horizon, with refraction.
  sun: SunEvents
  // -6°.
  civilTwilight: SunEvents
  solarNoon: string
}

const RAD = Math.PI / 180
const DEG = 180 / Math.PI
const MINUTE_MS = 60_000

// NOAA solar calculator (https://gml.noaa.gov/grad/solcalc/).
function solarPosition(ms: number) {
  const t = (ms / 86_400_000 + 2440587.5 - 2451545) / 36525
  const l0 = (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360
  const m = 357.52911 + t * (35999.05029 - 0.0001537 * t)
  const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t)
  const c =
    Math.sin(m * RAD) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * m * RAD) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * m * RAD) * 0.000289
  const omega = 125.04 - 1934.136 * t
  const apparentLong = l0 + c - 0.00569 - 0.00478 * Math.sin(omega * RAD)
  const meanObliquity = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60
  const obliquity = meanObliquity + 0.00256 * Math.cos(omega * RAD)
  const declination = Math.asin(Math.sin(obliquity * RAD) * Math.sin(apparentLong * RAD))
  const y = Math.tan((obliquity / 2) * RAD) ** 2
  const equationOfTimeMin =
    4 *
    DEG *
    (y * Math.sin(2 * l0 * RAD) -
      2 * e * Math.sin(m * RAD) +
      4 * e * y * Math.sin(m * RAD) * Math.cos(2 * l0 * RAD) -
      0.5 * y * y * Math.sin(4 * l0 * RAD) -
      1.25 * e * e * Math.sin(2 * m * RAD))
  return { declination, equationOfTimeMin }
}

const iso = (ms: number) => new Date(Math.round(ms / 1000) * 1000).toISOString()

function solarNoonMs(dayMs: number, lon: number) {
  let noon = dayMs + (720 - 4 * lon) * MINUTE_MS
  for (let i = 0; i < 2; i++) noon = dayMs + (720 - 4 * lon - solarPosition(noon).equationOfTimeMin) * MINUTE_MS
  return noon
}

// Minutes from solar noon to the sun reaching `altitudeDeg`; +/-Infinity if
// it stays above / below all day.
function halfArcMin(altitudeDeg: number, lat: number, aroundMs: number) {
  const { declination } = solarPosition(aroundMs)
  const phi = lat * RAD
  const cosH =
    (Math.sin(altitudeDeg * RAD) - Math.sin(phi) * Math.sin(declination)) / (Math.cos(phi) * Math.cos(declination))
  if (cosH < -1) return Infinity
  if (cosH > 1) return -Infinity
  return 4 * DEG * Math.acos(cosH)
}

function events(altitudeDeg: number, lat: number, noon: number): SunEvents {
  const first = halfArcMin(altitudeDeg, lat, noon)
  if (first === Infinity) return { state: 'alwaysUp', rise: null, set: null }
  if (first === -Infinity) return { state: 'alwaysDown', rise: null, set: null }
  // Refine with the declination at the event itself (~1 min at 60°N).
  const at = (sign: 1 | -1) => {
    const refined = halfArcMin(altitudeDeg, lat, noon + sign * first * MINUTE_MS)
    return noon + sign * (Number.isFinite(refined) ? refined : first) * MINUTE_MS
  }
  return { state: 'normal', rise: iso(at(-1)), set: iso(at(1)) }
}

// `date` is the local calendar day, YYYY-MM-DD.
export function sunTimes(date: string, lat: number, lon: number): SunTimes {
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number]
  const noon = solarNoonMs(Date.UTC(y, mo - 1, d), lon)
  return {
    date,
    sun: events(-0.833, lat, noon),
    civilTwilight: events(-6, lat, noon),
    solarNoon: iso(noon),
  }
}
